import { computed, ref, type Ref } from 'vue';
import type { CursorMeta, TrackMatchingCursor } from '@/types/types';

export type PagerDirection = 'first' | 'prev' | 'next' | 'last';

/** The request that fetches (and counts) the first page of a fresh result set. */
const FIRST_PAGE: TrackMatchingCursor = { token: null, last: false, withTotal: true };

/**
 * Cursor (search_after) paging state shared by the searchable list tables (TABASCO-1HT). A page is
 * identified by an opaque token rather than a row offset, so any page — including Last — is
 * reachable without tripping Elasticsearch's 10k from/size result window.
 *
 *  - `cursor`            the request sent for the *current* page (token / last flag / count-total).
 *  - `pageNumber`        1-based page for the "X / Y" label (a cursor has no inherent number).
 *  - `cachedTotal`       last known total; only refreshed when a response carries one (withTotal).
 *  - `hasNext`/`hasPrev` whether the BE returned an adjacent-page token — the navigation gate.
 *
 * Drive it by calling {@link captureMeta} on every settled response, {@link navigate} from the
 * pager's four actions, and {@link reset} whenever the result set changes (sort/filter/page size).
 */
export function useCursorPager(pageSize: Ref<number>) {
  const cursor = ref<TrackMatchingCursor>({ ...FIRST_PAGE });
  const pageNumber = ref(1);
  const cachedTotal = ref(0);
  const nextToken = ref<string | null>(null);
  const prevToken = ref<string | null>(null);

  // Page count drives the "X / Y" label only; navigation is gated on token presence (below), never
  // on the total — which may be a search_after estimate, null, or stale after rows change.
  const lastPage = computed(() =>
    Math.max(1, Math.ceil((cachedTotal.value || 0) / (pageSize.value || 20))),
  );

  // Enable/disable keys off whether the BE actually returned an adjacent-page token, so Next/Last
  // can't overrun the end (token null) and Prev/First can't underrun the start.
  const hasNext = computed(() => nextToken.value !== null);
  const hasPrev = computed(() => prevToken.value !== null);

  /** Snap back to a fresh, recounted first page — clearing the previous set's tokens and total. */
  function reset() {
    cursor.value = { ...FIRST_PAGE };
    pageNumber.value = 1;
    cachedTotal.value = 0;
    nextToken.value = null;
    prevToken.value = null;
  }

  /** Capture the adjacent-page tokens (and the total, when present) from a settled response. */
  function captureMeta(meta: CursorMeta | null | undefined) {
    if (!meta) return;
    if (typeof meta.total === 'number') {
      cachedTotal.value = meta.total;
    }
    nextToken.value = meta.next_cursor ?? null;
    prevToken.value = meta.prev_cursor ?? null;
  }

  /**
   * Map a pager action onto a cursor request. Next/Prev echo the current response's tokens and are
   * no-ops when that token is absent; First sends no cursor; Last asks the BE for the final page via
   * the `last` flag (served by its reverse-sort trick). Total isn't recounted on these hops.
   */
  function navigate(direction: PagerDirection) {
    switch (direction) {
      case 'first':
        cursor.value = { token: null, last: false, withTotal: false };
        pageNumber.value = 1;
        break;
      case 'next':
        if (!hasNext.value) return;
        cursor.value = { token: nextToken.value, last: false, withTotal: false };
        pageNumber.value = pageNumber.value + 1;
        break;
      case 'prev':
        if (!hasPrev.value) return;
        cursor.value = { token: prevToken.value, last: false, withTotal: false };
        pageNumber.value = Math.max(1, pageNumber.value - 1);
        break;
      case 'last':
        cursor.value = { token: null, last: true, withTotal: false };
        pageNumber.value = lastPage.value;
        break;
    }
  }

  return { cursor, pageNumber, cachedTotal, lastPage, hasNext, hasPrev, reset, captureMeta, navigate };
}
